/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Action, ActionCreators } from './actions';

export interface State {
  readonly latestChecksum?: string;
  readonly userLastViewedChecksum?: string;
  readonly contentChecksum?: string;
  readonly markdownContent: string;
  readonly isPanelOpen: boolean;
  readonly isRequestingLatestChecksum: boolean;
  readonly isRequestingUserChecksum: boolean;
  readonly isRequestingContent: boolean;
  readonly isMarkingViewed: boolean;
  readonly requestError?: string;
}

export const initialWhatsNewState: State = {
  latestChecksum: undefined,
  userLastViewedChecksum: undefined,
  contentChecksum: undefined,
  markdownContent: '',
  isPanelOpen: false,
  isRequestingLatestChecksum: false,
  isRequestingUserChecksum: false,
  isRequestingContent: false,
  isMarkingViewed: false,
  requestError: undefined,
};

export default function reducer(state: State = initialWhatsNewState, action: Action): State {
  let partialState: Partial<State> | undefined;

  switch (action.type) {
    case ActionCreators.RequestLatestChecksumPoll.type:
      partialState = {
        isRequestingLatestChecksum: true,
        requestError: undefined,
      };
      break;

    case ActionCreators.CancelLatestChecksumPoll.type:
      partialState = {
        isRequestingLatestChecksum: false,
      };
      break;

    case ActionCreators.ReceivedLatestChecksumSuccess.type:
      partialState = {
        latestChecksum: action.payload?.checksum ?? state.latestChecksum,
        isRequestingLatestChecksum: false,
        requestError: undefined,
      };
      break;

    case ActionCreators.ReceivedLatestChecksumError.type:
      partialState = {
        isRequestingLatestChecksum: false,
        requestError: action.payload?.message ?? "Failed to retrieve latest What's New checksum",
      };
      break;

    case ActionCreators.RequestUserChecksum.type:
      partialState = {
        isRequestingUserChecksum: true,
        requestError: undefined,
      };
      break;

    case ActionCreators.ReceivedUserChecksumSuccess.type:
      partialState = {
        userLastViewedChecksum: action.payload?.checksum ?? state.userLastViewedChecksum,
        isRequestingUserChecksum: false,
        requestError: undefined,
      };
      break;

    case ActionCreators.ReceivedUserChecksumError.type:
      partialState = {
        isRequestingUserChecksum: false,
        requestError: action.payload?.message ?? "Failed to retrieve What's New user checksum",
      };
      break;

    case ActionCreators.RequestWhatsNewContent.type:
      partialState = {
        isRequestingContent: true,
        requestError: undefined,
      };
      break;

    case ActionCreators.ReceivedWhatsNewContentSuccess.type:
      partialState = {
        contentChecksum: action.payload?.checksum ?? state.contentChecksum,
        markdownContent: action.payload?.markdownContent ?? state.markdownContent,
        isRequestingContent: false,
        requestError: undefined,
      };
      break;

    case ActionCreators.ReceivedWhatsNewContentError.type:
      partialState = {
        isRequestingContent: false,
        requestError: action.payload?.message ?? "Failed to retrieve What's New content",
      };
      break;

    case ActionCreators.OpenWhatsNewPanel.type:
      partialState = {
        isPanelOpen: true,
      };
      break;

    case ActionCreators.CloseWhatsNewPanel.type:
      partialState = {
        isPanelOpen: false,
      };
      break;

    case ActionCreators.RequestUpdateUserLastViewedChecksum.type:
      partialState = {
        isMarkingViewed: true,
        requestError: undefined,
      };
      break;

    case ActionCreators.UpdateUserLastViewedChecksumSuccess.type:
      partialState = {
        userLastViewedChecksum: action.payload?.checksum ?? state.userLastViewedChecksum,
        isMarkingViewed: false,
        requestError: undefined,
      };
      break;

    case ActionCreators.UpdateUserLastViewedChecksumError.type:
      partialState = {
        isMarkingViewed: false,
        requestError: action.payload?.message ?? "Failed to update What's New user checksum",
      };
      break;

    default:
      return state;
  }

  return { ...state, ...partialState };
}
